import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Lane } from 'src/common/components/Lane'
import { trackInitiateCheckout } from 'src/common/lib/segment'
import { prependAssetHostItem } from 'src/common/utils'
import useJoinEventWizard from 'src/hooks/useJoinEvent'
import { useAppSelector } from 'src/store/hooks'
import {
    clearBundle,
    removeOptionSelection,
    saveOptionSelection,
    updateOptionClicked
} from 'src/store/joinevent'
import { addToBundle } from 'src/store/joinevent/actions'
import {
    selectBundleChallengeItems,
    selectedItemsSelector
} from 'src/store/joinevent/selectors'

interface JoinRaceLanesSectionProps {
    challengeId: string | number
    isAlreadyJoined?: boolean
    isAddonOptional?: boolean
    isAddonOptionalReason?: string
    isLoggedIn: boolean
    onButtonPress?: () => void
}

export const JoinRaceLanesSection = ({
    challengeId,
    isAlreadyJoined = false,
    onButtonPress = () => null
}: JoinRaceLanesSectionProps) => {
    const dispatch = useDispatch()
    const router = useRouter()
    const event = useAppSelector((state) => state.joinevent)
    const rewards = useAppSelector((state) => state.joinevent.rewards)
    const addons = useAppSelector((state) => state.joinevent.addons)
    const user = useAppSelector((state) => state.user.data)
    const bundleChallengeItems = useAppSelector(selectBundleChallengeItems)
    const selectedItems = useAppSelector(selectedItemsSelector)
    const { hasAccessMessage, hasAccess, isNewPurchase, isAddonOptional, cart } =
        useJoinEventWizard()

    const isCheckoutButtonEnabled =
        hasAccess === false
            ? true
            : isAddonOptional === true
            ? false
            : selectedItems.length === 0

    // const isProductSelected = bundleChallengeItems.some(
    // 	(bItem) => bItem.challengeAddonId === i.challengeAddonId
    // )

    const onGoToCheckoutClicked = () => {
        if (!isNewPurchase || cart.bundle.paymentId !== null) {
            dispatch(clearBundle())
        }
        dispatch(addToBundle())
        trackInitiateCheckout({ productData: event, user })
        void router.push('/cart')
    }

    const getSumPriceOfSelectedItems = () => {
        return selectedItems.reduce((acc, obj) => {
            return acc + obj.itemPrice
        }, 0)
    }

    const RenderRewardMessage = () => {
        let message
        if (
            !isAlreadyJoined &&
            !isAddonOptional &&
            !rewards.some((reward) => reward.selected)
        )
            message = 'Select a reward to unlock this race.'
        else if (!hasAccess) message = hasAccessMessage
        else if (!isAlreadyJoined && !rewards.some((reward) => reward.selected))
            message =
                'Select a reward or join this race without a reward for a Digital Only Experience.'
        else if (isAlreadyJoined && !rewards.some((reward) => reward.selected))
            message =
                'You joined this as a digital only race - you can still add rewards & addons.'
        else if (isAlreadyJoined && rewards.some((reward) => reward.selected))
            message =
                'You have chosen a reward - you can still add additional rewards & addons.'
        return message ? (
            <div className="py-4 px-3 md:px-0 lg:pl-[56px] mb-5">
                <div
                    className="p-4 bg-yf-purple items-center text-yf-purple-100 leading-none rounded-md flex lg:inline-flex relative"
                    role="alert"
                >
                    <span className="font-medium mr-2 text-left flex-auto">
                        {message}
                    </span>
                    <span className="flex h-4 w-4 absolute top-[-5px] left-[-5px]">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yf-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-yf-green-500"></span>
                    </span>
                </div>
            </div>
        ) : (
            <></>
        )
    }

    useEffect(() => {
        rewards.forEach((item) => {
            const isRewardSelected = bundleChallengeItems.some(
                (bItem) => bItem.challengeAddonId === item.challengeAddonId
            )
            if (isRewardSelected) {
                const bundleItem: any = bundleChallengeItems.find(
                    (i) => i.challengeAddonId === item.challengeAddonId
                )
                const challengeAddonObj =
                    item.challengeAddonOptions?.find(
                        (i) =>
                            i.challengeAddonOptionId === bundleItem.challengeAddonOptionId
                    ) ?? null

                if (!item.selected && challengeAddonObj) {
                    dispatch(
                        updateOptionClicked({
                            challengeAddonId: item.challengeAddonId,
                            displayText: challengeAddonObj.displayText,
                            optionId: bundleItem.challengeAddonOptionId
                        })
                    )
                }
                dispatch(
                    saveOptionSelection({
                        challengeAddonId: item.challengeAddonId
                    })
                )
            }
        })

        addons.forEach((item) => {
            const isAddonSelected = bundleChallengeItems.some(
                (bItem) => bItem.challengeAddonId === item.challengeAddonId
            )
            if (isAddonSelected) {
                const bundleItem: any = bundleChallengeItems.find(
                    (i) => i.challengeAddonId === item.challengeAddonId
                )
                const challengeAddonObj =
                    item.challengeAddonOptions?.find(
                        (i) =>
                            i.challengeAddonOptionId === bundleItem.challengeAddonOptionId
                    ) ?? null

                if (!item.selected && challengeAddonObj) {
                    dispatch(
                        updateOptionClicked({
                            challengeAddonId: item.challengeAddonId,
                            displayText: challengeAddonObj?.displayText,
                            optionId: bundleItem.challengeAddonOptionId
                        })
                    )
                }
                dispatch(
                    saveOptionSelection({
                        challengeAddonId: item.challengeAddonId
                    })
                )
            }
        })

        return () => null
    }, [challengeId, bundleChallengeItems])

    return (
        <div id="rewards" className="pl-0">
            {!hasAccess ? (
                <div className="py-4 lg:pl-[56px] mb-5 cursor-pointer ">
                    <div
                        className="p-4 bg-yf-purple items-center text-yf-purple-100 leading-none lg:rounded-md flex lg:inline-flex relative"
                        role="alert"
                    >
                        <span className="font-medium mr-2 text-left flex-auto">
                            {hasAccessMessage}
                        </span>
                        <span className="flex h-4 w-4 absolute top-[-5px] left-[-5px]">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500"></span>
                        </span>
                    </div>
                </div>
            ) : (
                <RenderRewardMessage />
            )}

            <Lane
                title={
                    isAlreadyJoined
                        ? 'Current Selected Rewards'
                        : hasAccess
                        ? 'Select Your Rewards'
                        : 'Rewards available in this Virtual Race'
                }
                description="All Domestic Rewards Ship Free!"
                hideArrows
                data={rewards.map((i) => {
                    return {
                        cardType: 'merch',
                        isSoldOut: i.left != null && i.left === 0,
                        imageURL: prependAssetHostItem(i.challengeAddonAsset.imageUri),
                        price: `$${i.itemPrice}`,
                        title: i.itemName,
                        selected: i.selected,
                        onClick: () => {
                            if (i.challengeAddonOptions.length) {
                                if (i.selected) {
                                    dispatch(
                                        removeOptionSelection({
                                            challengeAddonId: i.challengeAddonId
                                        })
                                    )
                                } else if (hasAccess) {
                                    void router.push(
                                        `/races/${challengeId}/join/${i.challengeAddonId}`,
                                        null,
                                        {
                                            shallow: true,
                                            scroll: true
                                        }
                                    )
                                }
                            } else {
                                if (i.selected) {
                                    dispatch(
                                        removeOptionSelection({
                                            challengeAddonId: i.challengeAddonId
                                        })
                                    )
                                } else if (hasAccess) {
                                    dispatch(
                                        saveOptionSelection({
                                            challengeAddonId: i.challengeAddonId
                                        })
                                    )
                                }
                            }
                        }
                    }
                })}
            />

            {!!addons.length && (
                <Lane
                    title={
                        isAlreadyJoined
                            ? 'Addons'
                            : hasAccess
                            ? 'Select Your Optional Add-Ons'
                            : 'Add-Ons available in this Virtual Race'
                    }
                    hideArrows
                    data={addons.map((i) => {
                        return {
                            cardType: 'merch',
                            imageURL: prependAssetHostItem(
                                i.challengeAddonAsset.imageUri
                            ),
                            price: `$${i.itemPrice}`,
                            title: i.itemName,
                            selected: i.selected,
                            onClick: () => {
                                if (i.challengeAddonOptions.length) {
                                    if (i.selected) {
                                        dispatch(
                                            removeOptionSelection({
                                                challengeAddonId: i.challengeAddonId
                                            })
                                        )
                                    } else if (hasAccess) {
                                        void router.push(
                                            `/races/${challengeId}/join/${i.challengeAddonId}`,
                                            null,
                                            {
                                                shallow: true,
                                                scroll: true
                                            }
                                        )
                                    }
                                } else {
                                    if (i.selected) {
                                        dispatch(
                                            removeOptionSelection({
                                                challengeAddonId: i.challengeAddonId
                                            })
                                        )
                                    } else {
                                        dispatch(
                                            saveOptionSelection({
                                                challengeAddonId: i.challengeAddonId
                                            })
                                        )
                                    }
                                }
                            }
                        }
                    })}
                />
            )}

            <div className="px-4 md:px-10 flex justify-between items-center w-full fixed md:relative bottom-4 md:bottom-0 z-[2147483004] max-w-2xl">
                <button
                    className="block md:hidden rounded-full bg-yf-green text-white w-12 h-12 flex-shrink-0"
                    onClick={() => onButtonPress()}
                >
                    <i className="fal fa-chevron-double-down text-2xl mt-1"></i>
                </button>
                <button
                    disabled={isCheckoutButtonEnabled}
                    className={`py-3 flex-shrink-0 text-white font-medium w-5/6 max-w-lg px-6 rounded-lg focus:outline-none ${
                        !isCheckoutButtonEnabled
                            ? 'bg-yf-green focus:ring-yf-green-400 focus:ring-2 hover:bg-yf-green-600'
                            : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    onClick={() => onGoToCheckoutClicked()}
                >
                    {hasAccess === false && selectedItems.length === 0 ? (
                        <span className="flex justify-between ">
                            <span>$0</span>{' '}
                            <span>
                                Make a Selection{' '}
                                <i className="fal fa-long-arrow-right"></i>
                            </span>
                        </span>
                    ) : (
                        <span className="flex justify-between ">
                            <span>${getSumPriceOfSelectedItems()}</span>{' '}
                            <span>
                                Add to My Cart <i className="fal fa-long-arrow-right"></i>
                            </span>
                        </span>
                    )}
                </button>
            </div>
        </div>
    )
}
